import { RoleSchema } from '../user/schemas'

/**
 * A module containing schema validation and documentation related to Locations
 * @module ducks/location/schemas
 */

export const GeoPointSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    latitude: {
      type: 'number'
    },
    longitude: {
      type: 'number'
    }
  },
  required: ['latitude', 'longitude']
}

export const ThresholdSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minimum: 1
    },
    value: {
      type: 'number'
    }
  },
  required: ['name', 'value'],
  additionalProperties: false
}

export const RegionSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1
    },
    code: {
      type: 'string',
      minLength: 1
    }
  },
  required: ['code']
}

export const CountrySchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minLength: 1
    },
    code: {
      type: 'string',
      minLength: 2,
      maxLength: 2
    }
  },
  required: ['name', 'code']
}

export const AddressSchema = {
  /* eslint-disable camelcase */
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    address_line_1: {
      type: 'string',
      minimum: 1
    },
    address_line_2: {
      type: 'string'
    },
    city: {
      type: 'string',
      minimum: 1
    },
    county: {
      type: 'string',
      minimum: 1
    },
    region: RegionSchema,
    postal_code: {
      type: 'string',
      minimum: 1
    },
    country: CountrySchema,
    geo_point: GeoPointSchema
  },
  required: ['address_line_1', 'city', 'region', 'postal_code', 'country'],
  additionalProperties: false
  /* eslint-enable camelcase */
}

/** JSON schema definition for location creation */
export const LocationCreateRequestSchema = {
  /* eslint-disable camelcase */
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    name: {
      type: 'string',
      minimum: 1
    },
    device_id: {
      type: 'string',
      minimum: 1
    },
    address: AddressSchema,
    geo_point: GeoPointSchema,
    photo_bytes: {
      type: ['string', 'null']
    },
    weather_station_id: {
      type: 'string'
    }
  },
  required: ['name', 'address'],
  additionalProperties: false
  /* eslint-enable camelcase */
}

/** JSON schema definition for location get request */
export const LocationGetRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    location_id: {
      type: 'string',
      minimum: 1
    },
    include_metadata: {
      type: 'boolean'
    }
    /* eslint-enable camelcase */
  },
  required: ['location_id', 'include_metadata'],
  additionalProperties: false
}

/**
 * JSON schema definition for location share request
 */
export const LocationShareRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    location_id: {
      type: 'string',
      minimum: 1
    },
    role: RoleSchema,
    email_address: {
      type: 'string', // Not using the built-in `email` type to avoid any regex discrepancies from what we're using
      minimum: 1
    }
    /* eslint-enable camelcase */
  },
  required: ['location_id', 'role', 'email_address'],
  additionalProperties: false
}

/**
 * JSON schema definition for location share request
 */
export const LocationShareLimitedRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    location_id: {
      type: 'string',
      minimum: 1
    },
    expires: {
      type: 'string',
      minimum: 1
    },
    email_address: {
      type: 'string', // Not using the built-in `email` type to avoid any regex discrepancies from what we're using
      minimum: 1
    }
    /* eslint-enable camelcase */
  },
  required: ['location_id', 'expires', 'email_address'],
  additionalProperties: false
}

/** JSON schema definition for location update request */
export const LocationUpdateRequestSchema = {
  /* eslint-disable camelcase */
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    location_id: {
      // string location_id = 1;
      type: 'string',
      minimum: 1
    },
    name: {
      // google.protobuf.StringValue name = 2;
      type: 'string',
      minimum: 1
    },
    device_ids_to_add: {
      // repeated string device_ids_to_add = 3;
      type: 'array',
      items: {
        type: 'string',
        minimum: 1
      }
    },
    device_ids_to_remove: {
      // repeated string device_ids_to_remove = 4;
      type: 'array',
      items: {
        type: 'string',
        minimum: 1
      }
    },
    include_all_weather_stations: {
      type: 'boolean'
    },
    address: AddressSchema, // Address address = 5;
    geo_point: GeoPointSchema, // GeoPoint geo_point = 6;
    photo_bytes: {
      // google.protobuf.BytesValue photo_bytes = 7;
      type: ['string', 'null']
    }
  },
  required: ['location_id'],
  additionalProperties: false
}

export const LocationUpdateWeatherStationSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    location_id: {
      // string location_id = 1;
      type: 'string',
      minimum: 1
    },
    station_id: {
      type: 'string',
      minimum: 1
    }
  },
  required: ['location_id', 'station_id'],
  additionalProperties: false
}

/** JSON schema definition for location update request */
export const UpdateLocationThresholdRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    location_id: {
      // string location_id = 1;
      type: 'string',
      minimum: 1
    },
    location_threshold: ThresholdSchema // LocationThreshold location_threshold = 2;
  },
  required: ['location_id', 'location_threshold'],
  additionalProperties: false
}
/* eslint-enable camelcase */
